import { apiClient, type ApiResponse } from './client'

export type TestCompletionMode = 'LEARNER_ACTIVITY_ONLY' | 'WITH_TEACHER_FINALIZATION'
export type TestCompletionEndpointType =
  | 'LESSON'
  | 'BEFORE_NEXT_CHAPTER_TEST'
  | 'CHAPTER_END'
  | 'NEXT_CUMULATIVE_TEST'
  | 'COURSE_END'
export type TestCompletionRunStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'CANCEL_REQUESTED'
  | 'COMPLETED'
  | 'STOPPED'
  | 'PAUSED'
  | 'FAILED'
  | 'INTERRUPTED'

export interface TestCompletionEndpoint {
  type: TestCompletionEndpointType
  contentId: number
}

export interface TestCompletionCounts {
  total: number
  process: number
  skip: number
  success: number
  failed: number
}

export interface TestCompletionRunSummary {
  id: number
  learner: { id: number; name: string }
  status: TestCompletionRunStatus
  completionMode: TestCompletionMode
  endpoint: { type: TestCompletionEndpointType; title: string }
  counts: TestCompletionCounts
  currentOrdinal: number | null
  createdAt: string
}

export interface TestCompletionCapability {
  enabled: boolean
  operationMode: 'TEST' | 'PRODUCTION'
  activeRun: TestCompletionRunSummary | null
}

export interface TestCompletionPlanItem {
  ordinal: number
  type: 'LESSON' | 'CHAPTER_TEST' | 'CUMULATIVE_TEST' | 'UNIT_ASSESSMENT' | 'DESIGNATED_REVIEW'
  title: string
  action: 'PROCESS' | 'SKIP_ALREADY_COMPLETE'
  teacherRequirements?: {
    buildupGradeCount: number
    essentialCheckCount: number
    workbookGradeCount: number
    requiredAudioCount: number
    offlineInputComponentCount: number
  } | null
}

export interface TestCompletionPreflight {
  planHash: string
  learner: { id: number; name: string }
  completionMode: TestCompletionMode
  endpoint: { type: TestCompletionEndpointType; title: string }
  counts: TestCompletionCounts
  items: TestCompletionPlanItem[]
  blockingError: { code: string; itemOrdinal: number | null } | null
  requiresTypedConfirmation: boolean
}

export interface TestCompletionRunDetail extends TestCompletionRunSummary {
  initiatedBy: { id: number; name: string }
  activeOperator: { id: number; name: string }
  failureCode: string | null
  failureDetails?: Record<string, unknown> | null
  failureItemOrdinal: number | null
  startedAt: string | null
  stoppedAt: string | null
  completedAt: string | null
  version: number
  items: Array<TestCompletionPlanItem & {
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'SKIPPED' | 'FAILED'
    errorCode: string | null
    errorDetails?: Record<string, unknown> | null
  }>
}

export type TestCompletionRunHistory = Omit<
  TestCompletionRunDetail,
  'items' | 'version' | 'currentOrdinal' | 'startedAt' | 'stoppedAt'
>

interface PreflightRequest {
  completionMode: TestCompletionMode
  endpoint: TestCompletionEndpoint
}

const BASE = '/lc/admin/coaching/test-completion'

export const testCompletionApi = {
  capability: async (): Promise<TestCompletionCapability> => {
    const response = await apiClient.get<ApiResponse<TestCompletionCapability>>(`${BASE}/capability`)
    return response.data.data
  },
  preflight: async (learnerId: number, request: PreflightRequest): Promise<TestCompletionPreflight> => {
    const response = await apiClient.post<ApiResponse<TestCompletionPreflight>>(
      `${BASE}/learners/${learnerId}/preflight`,
      request,
    )
    return response.data.data
  },
  start: async (
    learnerId: number,
    request: PreflightRequest & { planHash: string; typedConfirmation?: string },
  ): Promise<TestCompletionRunDetail> => {
    const response = await apiClient.post<ApiResponse<TestCompletionRunDetail>>(
      `${BASE}/learners/${learnerId}/runs`,
      request,
    )
    return response.data.data
  },
  active: async (): Promise<TestCompletionRunDetail | null> => {
    const response = await apiClient.get<ApiResponse<TestCompletionRunDetail | null>>(`${BASE}/runs/active`)
    return response.data.data ?? null
  },
  detail: async (runId: number): Promise<TestCompletionRunDetail> => {
    const response = await apiClient.get<ApiResponse<TestCompletionRunDetail>>(`${BASE}/runs/${runId}`)
    return response.data.data
  },
  cancel: async (runId: number): Promise<TestCompletionRunDetail> => {
    const response = await apiClient.post<ApiResponse<TestCompletionRunDetail>>(`${BASE}/runs/${runId}/cancel`)
    return response.data.data
  },
  resumePreflight: async (runId: number): Promise<TestCompletionPreflight> => {
    const response = await apiClient.post<ApiResponse<TestCompletionPreflight>>(`${BASE}/runs/${runId}/resume-preflight`)
    return response.data.data
  },
  resume: async (runId: number, planHash: string): Promise<TestCompletionRunDetail> => {
    const response = await apiClient.post<ApiResponse<TestCompletionRunDetail>>(
      `${BASE}/runs/${runId}/resume`,
      { planHash },
    )
    return response.data.data
  },
  history: async (learnerId: number): Promise<TestCompletionRunHistory[]> => {
    const response = await apiClient.get<ApiResponse<TestCompletionRunHistory[]>>(
      `${BASE}/learners/${learnerId}/runs`,
    )
    return response.data.data
  },
}
